import type { SectionProps } from "../context";
import { field } from "../context";
import { eventInstant, googleCalendarUrl, parseEventDate, timeLeft } from "../helpers";
import { Icon } from "../icons";
import { Container, MultiLine, REVEAL, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

const UNITS = [
  ["d", "Hari"],
  ["h", "Jam"],
  ["m", "Menit"],
  ["s", "Detik"],
] as const;

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Countdown to a dated moment (akad, resepsi, launch). Rendered with the values at render time; on live
 * sites the runtime ticks every `[data-baze-countdown]` once per second (the engine ships no script).
 * Once the moment has passed the numbers give way to `doneText`.
 */
export function Countdown({ node, ctx }: SectionProps<"countdown">) {
  const c = node.content;
  const at = eventInstant(c.date, c.time, c.timeNote);
  const date = parseEventDate(c.date);
  const left = at === null ? null : timeLeft(at, ctx.now);
  const calendar = c.showCalendar !== false && date ? googleCalendarUrl({ name: c.title, date: c.date, startTime: c.time, timeNote: c.timeNote }, ctx.site.name) : null;
  const done = c.doneText?.trim() || "Hari yang dinanti telah tiba!";
  const boxes = node.variant !== "minimal";
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size="narrow" className="text-center">
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
        {date ? (
          <p className="mt-4 font-medium text-site-muted">
            <time dateTime={c.date}>{date.long}</time>
            {c.time ? ` · ${c.time.replace(":", ".")}${c.timeNote ? ` ${c.timeNote}` : ""}` : ""}
          </p>
        ) : null}
        {left ? (
          <div className={cx(REVEAL, "mt-10")} data-baze-countdown={at ?? undefined}>
            <p data-baze-countdown-done="" hidden={!left.done} className="font-site-heading text-2xl text-site-primary @3xl:text-3xl" {...field(ctx, "content.doneText")}>
              {done}
            </p>
            <ol hidden={left.done} aria-label="Hitung mundur" className={cx(boxes ? "grid grid-cols-4 gap-2.5 @md:gap-4" : "flex items-baseline justify-center gap-3 @md:gap-6")}>
              {UNITS.map(([key, label]) => (
                <li
                  key={key}
                  className={cx(
                    boxes
                      ? "relative flex flex-col items-center rounded-site-md border border-site-border bg-site-surface px-1 py-4 shadow-site-sm @md:py-6"
                      : "flex flex-col items-center",
                  )}
                >
                  <span data-unit={key} className={cx("font-site-heading leading-none font-semibold tabular-nums text-site-text", boxes ? "text-3xl @md:text-5xl" : "text-4xl @md:text-6xl", key === "s" && "baze-tick")}>
                    {key === "d" ? left.d : pad(left[key])}
                  </span>
                  <span className="mt-2 text-[0.6875rem] font-semibold tracking-[0.18em] text-site-muted uppercase @md:text-xs">{label}</span>
                </li>
              ))}
            </ol>
          </div>
        ) : ctx.editor ? (
          <p className="mt-8 text-sm text-site-muted">Isi tanggal acara untuk menampilkan hitung mundur.</p>
        ) : null}
        <MultiLine ctx={ctx} text={c.description} path="content.description" className="mx-auto mt-8 max-w-xl leading-relaxed text-site-muted" />
        {calendar ? (
          <div className="mt-8 flex justify-center">
            <SiteLink
              ctx={ctx}
              href={calendar}
              fallback="none"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-site-button border border-site-border px-5 text-sm font-semibold text-site-text transition hover:border-site-primary hover:text-site-primary motion-reduce:transition-none"
              ariaLabel={`Simpan ${c.title} ke Google Calendar`}
            >
              <Icon name="calendar" className="size-4" />
              <span>Simpan ke kalender</span>
            </SiteLink>
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}
